import { STATES_AND_TERRITORIES_ABBREVIATED } from '@zg-rentals/web-base';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import {
  Alert,
  Box,
  Button,
  DropdownSelect,
  FormField,
  Heading,
  Icon,
  Input,
  Label,
  LabeledControl,
  Modal,
  Radio,
  Select,
  Text,
} from '@zillow/constellation-10';
import { IconInfoFilled } from '@zillow/constellation-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  emitCancelBillingContactInfoEvent,
  emitSaveCloseBillingContactInfoEvent,
  EVENT_IDS,
} from '../../model/ClickstreamEvents';
import type { AdManagementBillingContactDto } from '@zg-rentals/ts-adproducts-client';
import {
  AdManagementInvoiceDeliveryMethod,
  AdManagementInvoiceOwnerRecipientType,
  AdproductsUserBillingContactApiHandler_getBillingContact,
  AdproductsUserBillingContactApiHandler_saveBillingContact,
} from '@zg-rentals/ts-adproducts-client';

export type PaymentInfoModalProps = {
  accountNumber: string;
  isMultiFamily: boolean;
  onClose: () => void;
};

const ChangeBillingInfoModal = ({ accountNumber, isMultiFamily, onClose }: PaymentInfoModalProps) => {
  const [accountBillingEmail, setAccountBillingEmail] = useState('');
  const [billingStreet, setBillingStreet] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [invoiceDeliveryMethod, setInvoiceDeliveryMethod] = useState<AdManagementInvoiceDeliveryMethod>();
  const [invoiceOwnerRecipient, setInvoiceOwnerRecipient] = useState<AdManagementInvoiceOwnerRecipientType>();

  const [billingAddress2, setBillingAddress2] = useState('');
  const [apiErrorMessage, setApiErrorMessage] = useState('');

  const [isDisabled, setIsDisabled] = useState(false);
  const [isSingleFamilyZuoraAccount, setIsSingleFamilyZuoraAccount] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const { user } = useRentalManagerUser();

  const [invoiceMethod, setInvoiceMethod] = useState('');
  const [systemDisplay, setSystemDisplay] = useState('');

  const invoiceDeliveryMethods = useMemo(() => ['Email', 'Upload'], []);
  const systemOptions = useMemo(
    () => [
      { value: AdManagementInvoiceDeliveryMethod.UPLOAD_OPS_TECH, label: 'Ops Technology' },
      { value: AdManagementInvoiceDeliveryMethod.UPLOAD_VENDOR_CAFE, label: 'Vendor Cafe' },
      { value: AdManagementInvoiceDeliveryMethod.UPLOAD_COUPA, label: 'Coupa' },
      { value: AdManagementInvoiceDeliveryMethod.UPLOAD_PAYMODE, label: 'Paymode' },
    ],
    [],
  );

  const showSytemtOptions = invoiceMethod && invoiceMethod != invoiceDeliveryMethods[0];
  const showInvoiceDeliverField = useRef<boolean>(isMultiFamily);
  const addressFieldsDisabled = isDisabled || isSingleFamilyZuoraAccount;

  useEffect(() => {
    setIsDisabled(true);
    AdproductsUserBillingContactApiHandler_getBillingContact({ accountNumber: accountNumber })
      .then((dto) => {
        setAccountBillingEmail(dto.accountBillingEmail);
        setBillingStreet(dto.billingStreet);
        setBillingState(dto.billingState);
        setBillingCity(dto.billingCity);
        setBillingZip(dto.billingZip);
        setInvoiceDeliveryMethod(dto.invoiceDeliveryMethod);
        setInvoiceOwnerRecipient(dto.invoiceOwnerRecipient || AdManagementInvoiceOwnerRecipientType.PROPERTY);
        setIsSingleFamilyZuoraAccount(dto.isSingleFamilyZuoraAccount ?? false);

        showInvoiceDeliverField.current = !dto.invoiceDeliveryMethod || showInvoiceDeliverField.current;
        const delivery = dto.invoiceDeliveryMethod;
        setInvoiceMethod(
          delivery ? (delivery.includes('UPLOAD') ? invoiceDeliveryMethods[1] : invoiceDeliveryMethods[0]) : '',
        );
        setSystemDisplay(systemOptions.find((opt) => opt.value === delivery)?.label || '');
        setIsDisabled(false);
        return;
      })
      .catch((err: Error) => setApiErrorMessage(err.message || 'Error fetching billing data. Please try again later.'));
  }, [accountNumber]);

  const Footer = () => {
    const onSave = () => {
      setSubmitAttempted(true);
      const dto = {
        accountBillingEmail,
        billingStreet: `${billingStreet}${billingAddress2 ? ` ${billingAddress2}` : ''}`,
        billingCity,
        billingState,
        billingZip,
        invoiceDeliveryMethod,
        invoiceOwnerRecipient,
        zuoraAccountNumber: accountNumber,
      } as AdManagementBillingContactDto;

      const formError = Object.values(dto).filter((value) => value === '' || value === undefined).length;
      if (!formError) {
        setIsDisabled(true);
        AdproductsUserBillingContactApiHandler_saveBillingContact(dto)
          .then(onClose)
          .then(() => emitSaveCloseBillingContactInfoEvent({ user }))
          .catch((err: Error) =>
            setApiErrorMessage(err.message || 'Error saving billing data. Please try again later.'),
          );
        setIsDisabled(false);
      }
    };

    return (
      <Box css={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          tone="brand"
          emphasis="outlined"
          onClick={() => {
            emitCancelBillingContactInfoEvent({ user });
            onClose();
          }}
          disabled={isDisabled}
          data-eventid={EVENT_IDS.CANCEL_BILLING_CONTACT_INFO}
        >
          Cancel
        </Button>
        <Button
          tone="brand"
          emphasis="filled"
          onClick={onSave}
          disabled={isDisabled}
          css={{ marginLeft: 'sm' }}
          data-eventid={EVENT_IDS.SAVE_CLOSE_BILLING_CONTACT_INFO}
        >
          Save
        </Button>
      </Box>
    );
  };

  return (
    <Modal
      header={
        <Heading level={6} css={{ textAlign: 'center' }}>
          Update billing information
        </Heading>
      }
      footer={<Footer />}
      onOpenChange={(open) => !open && onClose()}
      defaultOpen={true}
      size={{ base: 'fullScreen', lg: 'md' }}
      body={
        <>
          {apiErrorMessage && <Alert tone="critical">{apiErrorMessage}</Alert>}
          <FormField
            required
            error={submitAttempted && !accountBillingEmail}
            disabled={isDisabled}
            css={{ marginBottom: 'sm' }}
          >
            <Label>Billing email</Label>
            <Input
              value={accountBillingEmail}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccountBillingEmail(e.target.value)}
              placeholder="example@zillow.com"
            />
          </FormField>
          {isSingleFamilyZuoraAccount && (
            <Alert tone="info" css={{ marginBottom: 'sm' }}>
              Address fields are managed by your Zuora account and cannot be edited here.
            </Alert>
          )}
          <Heading level={6}>Billing Address</Heading>
          <Box css={{ marginLeft: 'md' }}>
            <FormField required error={submitAttempted && !billingStreet} disabled={addressFieldsDisabled}>
              <Label>Address line 1</Label>
              <Input
                value={billingStreet}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBillingStreet(e.target.value)}
                placeholder="Enter address"
              />
            </FormField>
            <FormField disabled={addressFieldsDisabled} css={{ marginTop: 'sm' }}>
              <Label>Address line 2</Label>
              <Input
                value={billingAddress2}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBillingAddress2(e.target.value)}
                placeholder="Additional address"
              />
            </FormField>
            <Box css={{ display: 'flex' }}>
              <FormField
                required
                error={submitAttempted && !billingCity}
                disabled={addressFieldsDisabled}
                css={{ marginRight: 'xs' }}
              >
                <Label>City</Label>
                <Input
                  value={billingCity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBillingCity(e.target.value)}
                  placeholder="City"
                />
              </FormField>
              <FormField
                required
                error={submitAttempted && !billingState}
                disabled={addressFieldsDisabled}
                css={{ marginRight: 'sm' }}
              >
                <Label>State</Label>
                <Select
                  value={billingState}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setBillingState(e.target.value)}
                >
                  {STATES_AND_TERRITORIES_ABBREVIATED.map((stateCode) => (
                    <option key={stateCode} value={stateCode}>
                      {stateCode}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField required error={submitAttempted && !billingZip} disabled={addressFieldsDisabled}>
                <Label>Zip</Label>
                <Input
                  value={billingZip}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBillingZip(e.target.value)}
                  placeholder="Zip"
                />
              </FormField>
            </Box>
          </Box>
          {accountNumber.length < 12 && (
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: 'xs',
                backgroundColor: 'gray.100',
                borderRadius: 'md',
                padding: 'sm',
                marginTop: 'sm',
              }}
            >
              <Icon size="md" css={{ color: 'gray.800', flexShrink: 0 }}>
                <IconInfoFilled />
              </Icon>
              <Text textStyle="body">This billing address will be used for tax purposes.</Text>
            </Box>
          )}

          {showInvoiceDeliverField && (
            <>
              <FormField
                required
                label={<Label>How would you like us to deliver the invoice</Label>}
                css={{ marginTop: 'sm' }}
                control={
                  <DropdownSelect
                    placeholder="Select Option"
                    value={invoiceMethod}
                    onChange={(option: string) => {
                      if (option === 'Email') setInvoiceDeliveryMethod(AdManagementInvoiceDeliveryMethod.EMAIL);
                      setInvoiceMethod(option);
                    }}
                    options={invoiceDeliveryMethods}
                  />
                }
                error={submitAttempted && !invoiceMethod}
                disabled={isDisabled}
              />
              {showSytemtOptions && (
                <>
                  <FormField
                    required
                    label={<Label>What system do you use?</Label>}
                    css={{ marginTop: 'sm' }}
                    control={
                      <DropdownSelect
                        value={systemDisplay}
                        placeholder="Select Option"
                        onChange={(opt: string) => {
                          setInvoiceDeliveryMethod(systemOptions.find((sys) => sys.label == opt)?.value);
                          setSystemDisplay(opt);
                        }}
                        options={systemOptions.map((method) => method.label)}
                      />
                    }
                    error={submitAttempted && !invoiceDeliveryMethod}
                    disabled={isDisabled}
                  />
                </>
              )}
              <FormField
                disabled={isDisabled}
                css={{ marginTop: 'sm' }}
                label={
                  <Label>
                    Would you like this property to be billed on one invoice? <Text css={{ color: 'red.600' }}>*</Text>
                    <br />
                    <Text textStyle="fineprint">Changes will take effect when their next invoice is generated</Text>
                  </Label>
                }
                control={
                  <>
                    <LabeledControl
                      label={<Label>No, bill to property (individual invoice)</Label>}
                      control={
                        <Radio
                          name="LabeledControl-billing"
                          checked={invoiceOwnerRecipient === AdManagementInvoiceOwnerRecipientType.PROPERTY}
                        />
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setInvoiceOwnerRecipient(
                          e.target.checked
                            ? AdManagementInvoiceOwnerRecipientType.PROPERTY
                            : AdManagementInvoiceOwnerRecipientType.MANAGEMENT_COMPANY,
                        )
                      }
                    />
                    <LabeledControl
                      label={<Label>Yes, bill to the management company (consolidated invoice)</Label>}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setInvoiceOwnerRecipient(
                          e.target.checked
                            ? AdManagementInvoiceOwnerRecipientType.MANAGEMENT_COMPANY
                            : AdManagementInvoiceOwnerRecipientType.PROPERTY,
                        )
                      }
                      control={
                        <Radio
                          name="LabeledControl-billing"
                          checked={invoiceOwnerRecipient === AdManagementInvoiceOwnerRecipientType.MANAGEMENT_COMPANY}
                        />
                      }
                    />
                  </>
                }
              />
            </>
          )}
        </>
      }
    />
  );
};

export default ChangeBillingInfoModal;
